import { Component, computed, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { map, Observable, Subscription } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { ModalService } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { Asset } from '@app/models/asset/asset';
import { FinalQCUpdateCommand, Roll } from '@app/models/knitting/roll';
import { Fabric, WorkOrder } from '@app/models/order/work-order';
import { DyeingColor } from '@app/models/production/dyeing/dyeing-color';
import { ProductionCard, ProductionCardSearchCommand } from '@app/models/production/production-card';
import { WorkOrderQCSummaryDto } from '@app/models/production/qc-summary';
import { ProductionCardsProxyService } from '@app/proxies/production/production-card.proxy.service';
import { ReportProxyService } from '@app/proxies/production/report.proxy.service';
import { RollProxyService } from '@app/proxies/production/roll.proxy.service';
import { RollLabelProxyService } from '@app/proxies/production/roll-label.proxy.service';
import { BarcodeScannerService } from '@app/services/barcode-scanner.service';
import { IndividualProductionCardService } from '@app/services/individual-production-card.service';
import { KnittingUtil } from '@app/utilities/knitting-util';
import { unitOfMeasureToDisplayUnit } from '@app/utilities/unit-of-measure.util';
import { BalanceoModalComponent } from '@app/components/finishing/_modals/balanceo-modal/balanceo-modal.component';
import { CuadrajeModalComponent } from '@app/components/finishing/_modals/cuadraje-modal/cuadraje-modal.component';
import { RollsDisplayTableComponent } from '@app/components/production-card/tables/rolls-display-table/rolls-display-table.component';

@Component({
  selector: 'app-roll-final-qc',
  templateUrl: './roll-final-qc.component.html',
  styleUrl: './roll-final-qc.component.scss',
})
export class RollFinalQcComponent implements OnInit, OnDestroy {
  @ViewChild('rollsTable') rollsTable!: RollsDisplayTableComponent;

  home: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

  isLoading = signal(0);

  /** Picked in the autocomplete. Nothing is loaded until the search button is pressed. */
  selectedProductionCard: ProductionCard | string | null = null;
  /** The card currently loaded on the page. */
  productionCard = signal<ProductionCard | null>(null);

  rolls = signal<Roll[]>([]);

  // QC Summary
  qcSummary = signal<WorkOrderQCSummaryDto | null>(null);

  rollFilterControl = new FormControl('');
  private rollFilter = toSignal(this.rollFilterControl.valueChanges, { initialValue: this.rollFilterControl.value });
  private scannerSubscription: Subscription | null = null;

  // Batch GSM input
  batchGsmValue: number | null = null;

  filteredRolls = computed(() => {
    const allRolls = this.rolls();
    const filterValue = this.rollFilter()?.toLowerCase() || '';

    const rollsWithFinalWeight = allRolls.filter(
      // (roll) => roll.finalWeight && roll.finalWeight.weightInLbs > 0 && _.isNil(roll.productionCardReference),
      (roll) => roll.finalWeight && roll.finalWeight.weightInLbs,
    );

    const sortedRolls = KnittingUtil.sortRollsByFinalWeightOrder(rollsWithFinalWeight);

    if (!filterValue) {
      return sortedRolls;
    }

    return sortedRolls.filter((roll) => roll.number?.toLowerCase().includes(filterValue));
  });

  fabrics = computed<Fabric[]>(() => {
    return this.individualProductionCardService.fabrics;
  });

  //#region Production card details, shown in the Details panel
  get workOrder(): WorkOrder | null {
    return this.individualProductionCardService.workOrder;
  }

  get dyeingAsset(): Asset | null {
    return this.individualProductionCardService.dyeingAsset;
  }

  get dyeingColor(): DyeingColor | null {
    return this.individualProductionCardService.dyeingColor;
  }

  get clientPO(): string {
    return this.individualProductionCardService.clientPO;
  }
  //#endregion

  constructor(
    private productionCardProxy: ProductionCardsProxyService,
    private rollProxyService: RollProxyService,
    private rollLabelProxyService: RollLabelProxyService,
    private individualProductionCardService: IndividualProductionCardService,
    private barcodeScannerService: BarcodeScannerService,
    private readonly reportProxyService: ReportProxyService,
    private readonly modalService: ModalService,
  ) {}

  ngOnInit(): void {
    this.scannerSubscription = this.barcodeScannerService.barcode$.subscribe((barcode) => {
      this.rollFilterControl.setValue(barcode);
    });
  }

  ngOnDestroy(): void {
    this.scannerSubscription?.unsubscribe();
  }

  public fetchProductionCards = (query: string): Observable<ProductionCard[]> =>
    this.productionCardProxy.search({ productionCardNumber: query } as ProductionCardSearchCommand).pipe(map(({ data }) => data ?? []));

  /**
   * The autocomplete also holds whatever was typed, so free text that never matched a suggestion
   * lands here as a plain string. That is not a card, so there is nothing to load from it.
   */
  get pendingProductionCard(): ProductionCard | null {
    const selected = this.selectedProductionCard;

    return typeof selected === 'object' && selected !== null && !!selected.productionCardNumber ? selected : null;
  }

  searchProductionCard(): void {
    const card = this.pendingProductionCard;
    if (!card) return;

    this.productionCard.set(card);
    this.rollFilterControl.setValue('');
    this.loadRolls(card.productionCardNumber!);
    this.initializeProductionCardService(card.productionCardNumber!);
    this.loadQCSummary(card.workOrderReference);
  }

  private initializeProductionCardService(productionCardNumber: string): void {
    this.isLoading.update((v) => v + 1);
    this.individualProductionCardService.initialize(productionCardNumber).subscribe({
      next: () => {
        this.isLoading.update((v) => v - 1);
      },
      error: () => {
        this.isLoading.update((v) => v - 1);
        GlobalMessageUtil.error('Error', 'Failed to load production card details');
      },
    });
  }

  loadRolls(productionCardNumber: string): void {
    this.isLoading.update((v) => v + 1);
    this.rollProxyService.getRollsCurrentlyLinkedToProductionCardNumber(productionCardNumber).subscribe({
      next: (response) => {
        this.isLoading.update((v) => v - 1);
        if (response.data) {
          this.rolls.set(response.data);
        }
      },
      error: () => {
        this.isLoading.update((v) => v - 1);
      },
    });
  }

  loadQCSummary(workOrderReference: string): void {
    if (!workOrderReference) {
      this.qcSummary.set(null);
      return;
    }

    this.productionCardProxy.getWorkOrderQCSummary(workOrderReference).subscribe({
      next: (response) => {
        if (response.data) {
          this.qcSummary.set(response.data);
        }
      },
      error: () => {
        this.qcSummary.set(null);
      },
    });
  }

  refreshQCSummary(): void {
    const pc = this.productionCard();
    if (pc?.workOrderReference) {
      this.loadQCSummary(pc.workOrderReference);
    }
  }

  clearFilter(): void {
    this.rollFilterControl.setValue('');
  }

  // Batch action methods
  approveAll(): void {
    if (this.rollsTable) {
      this.rollsTable.approveAllPendingRolls();
    }
  }

  rejectAll(): void {
    if (this.rollsTable) {
      this.rollsTable.rejectAllPendingRolls();
    }
  }

  applyBatchGsm(): void {
    if (this.rollsTable && this.batchGsmValue !== null && this.batchGsmValue > 0) {
      this.rollsTable.setGsmForAllPendingRolls(this.batchGsmValue);
    }
  }

  getPendingChangeCount(): number {
    return this.rollsTable?.getPendingChangeCount() ?? 0;
  }

  getEditableRollCount(): number {
    return this.rollsTable?.getEditableRollCount() ?? 0;
  }

  saveAllPendingChanges(): void {
    if (!this.rollsTable) return;

    const pendingChanges = this.rollsTable.getPendingChanges();
    if (pendingChanges.length === 0) {
      GlobalMessageUtil.warn('Warning', 'No pending changes to save');
      return;
    }

    this.isLoading.update((v) => v + 1);

    const commands: FinalQCUpdateCommand[] = pendingChanges.map((change) => ({
      rollNumber: change.roll.number!,
      isApproved: change.isApproved,
      finalGsm: change.finalGsm,
      finalWidth: change.finalWidth,
      finalLengthInYards: change.finalLengthInYards,
      rollCategory: change.rollCategory,
      hold: change.hold,
    }));

    this.rollProxyService.batchFinalQc(commands).subscribe({
      next: (response) => {
        this.isLoading.update((v) => v - 1);

        if (response.data) {
          // Update rolls with response data
          const updatedRolls = new Map<string, Roll>();
          response.data.forEach((roll) => {
            updatedRolls.set(roll.number!, roll);
          });

          this.rolls.update((currentRolls) =>
            currentRolls.map((r) => (r.number && updatedRolls.has(r.number) ? updatedRolls.get(r.number)! : r)),
          );
        }

        // Clear pending changes
        this.rollsTable.clearPendingChanges();

        // Refresh QC summary to reflect the changes
        this.refreshQCSummary();

        GlobalMessageUtil.success('Success', `${pendingChanges.length} roll(s) have been updated`);
      },
      error: () => {
        this.isLoading.update((v) => v - 1);
      },
    });
  }

  /**
   * The Customer PO a color of this Work Order belongs to. A Work Order can carry garment details
   * for more than one PO, so this is scoped to the color of the production card actually loaded —
   * same filter `GetCustomerPurchaseOrderCuadrajeAsync`/`GetBalanceReportAsync` already apply
   * server-side. If that color spans more than one PO, the first one found is used.
   */
  private resolveProductOrderNumber(): string | null {
    const workOrder = this.workOrder;
    const colorReference = this.productionCard()?.colorReference;
    if (!workOrder || !colorReference) return null;

    for (const fabric of workOrder.fabrics) {
      const match = fabric.garmetDetails?.find((detail) => detail.colorReference === colorReference && detail.productOrderNumber);
      if (match?.productOrderNumber) {
        return match.productOrderNumber;
      }
    }

    return null;
  }

  /**
   * The Cuadraje reads a Customer PO, and the only way to reach one is through the color of a
   * loaded production card. Mirrors `resolveProductOrderNumber` instead of just checking that a
   * card is selected, so the button is also disabled for a card whose color maps to no Customer PO
   * — the case that used to answer the click with an error toast.
   */
  get canOpenCuadraje(): boolean {
    return this.resolveProductOrderNumber() !== null;
  }

  /**
   * Cuadraje of the Customer PO this production card's color belongs to. Opens instantly — the
   * modal fetches and adapts its own data in `ngOnInit`, so the loading state lives there, not here.
   */
  openCuadraje(): void {
    const productOrderNumber = this.resolveProductOrderNumber();
    if (!productOrderNumber) {
      GlobalMessageUtil.error('Error', 'No se encontró una Customer PO para el color de esta production card');
      return;
    }

    this.modalService
      .open(
        CuadrajeModalComponent,
        { modalClass: 'modal_extralarge' },
        {
          productOrderNumber,
          workOrderUnit: unitOfMeasureToDisplayUnit(this.workOrder?.uom),
          currentColorReference: this.productionCard()?.colorReference,
        },
      )
      .subscribe();
  }

  /**
   * Balanceo of this production card: how much Rib and Tubular is off the ratio the body fabric
   * really reached. Opens instantly — same self-fetching pattern as Cuadraje.
   */
  openBalanceo(): void {
    const productionCardId = this.productionCard()?.id;
    if (!productionCardId) {
      GlobalMessageUtil.error('Error', 'No production card selected');
      return;
    }

    this.modalService.open(BalanceoModalComponent, { modalClass: 'modal_extralarge' }, { productionCardId }).subscribe();
  }

  printBalanceSummary(): void {
    const pc = this.productionCard();
    if (!pc?.id) {
      GlobalMessageUtil.error('Error', 'No production card selected');
      return;
    }

    this.isLoading.update((v) => v + 1);
    this.reportProxyService.getProductionCardBalanceReport(pc.id).subscribe({
      next: (response) => {
        this.isLoading.update((v) => v - 1);
        const blob = new Blob([response], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      },
      error: () => {
        this.isLoading.update((v) => v - 1);
      },
    });
  }

  printAllFinalLabels(): void {
    const pc = this.productionCard();
    if (!pc?.id) {
      GlobalMessageUtil.error('Error', 'No production card selected');
      return;
    }

    this.isLoading.update((v) => v + 1);
    this.rollLabelProxyService.getFinalLabelsByProductionCardReference(pc.id).subscribe({
      next: (response) => {
        this.isLoading.update((v) => v - 1);
        const blob = new Blob([response], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);

        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.left = '-9999px';
        iframe.style.top = '-9999px';
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.visibility = 'hidden';

        document.body.appendChild(iframe);

        iframe.onload = () => {
          try {
            setTimeout(() => {
              if (iframe.contentWindow) {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
              }
            }, 500);
          } catch (e) {
            console.error('Error printing PDF:', e);
            window.open(url, '_blank');
            setTimeout(() => {
              document.body.removeChild(iframe);
              URL.revokeObjectURL(url);
            }, 1000);
          }
        };

        iframe.src = url;
      },
      error: () => {
        this.isLoading.update((v) => v - 1);
      },
    });
  }
}
